// src/components/OrderTimeline.tsx
// A visual progress stepper for a single order, driven by the Square fulfillment
// state — the reliable signal the buyer actually tracks (see
// utils/fulfillmentStatus.ts). Renders horizontally on sm+ and stacks vertically
// on mobile. A canceled or failed order shows a distinct banner instead of the
// progress track (the stages no longer apply).
//
// There is no "Delivered" stage: Printful's package_shipped webhook flips the
// order to COMPLETED ("Shipped") and we get no delivery signal, so the track
// intentionally ends at Shipped and defers live delivery updates to Track.

import React from "react";

interface OrderTimelineProps {
  /** Square fulfillment state: PROPOSED | RESERVED | PREPARED | COMPLETED | ... */
  fulfillmentState?: string;
  /** Square order state — used only to detect a CANCELED order. */
  orderState?: string;
}

const STEPS = [
  { key: "placed", label: "Order Placed" },
  { key: "production", label: "In Production" },
  { key: "prepared", label: "Ready to Ship" },
  { key: "shipped", label: "Shipped" },
] as const;

// Map a fulfillment state to the index of the furthest-reached step.
const stateToIndex = (state?: string): number => {
  switch (state) {
    case "COMPLETED":
      return 3;
    case "PREPARED":
      return 2;
    case "RESERVED":
      return 1;
    // PROPOSED / OPEN / unknown-but-present → the order has been placed.
    default:
      return 0;
  }
};

const OrderTimeline: React.FC<OrderTimelineProps> = ({
  fulfillmentState,
  orderState,
}) => {
  const isCanceled =
    orderState === "CANCELED" || fulfillmentState === "CANCELED";
  const isFailed = fulfillmentState === "FAILED";

  if (isCanceled || isFailed) {
    return (
      <div
        className={`rounded-md px-4 py-3 text-sm lora-text ${
          isFailed
            ? "bg-red-50 text-red-800 border border-red-200"
            : "bg-gray-100 text-gray-600 border border-gray-200"
        }`}
      >
        {isFailed
          ? "There was an issue with this order. Please contact support and we'll make it right."
          : "This order was canceled. No further action is needed."}
      </div>
    );
  }

  const current = stateToIndex(fulfillmentState);
  const lastIndex = STEPS.length - 1;

  return (
    <ol className="flex flex-col sm:flex-row gap-4 sm:gap-0">
      {STEPS.map((step, i) => {
        const reached = i <= current;
        const isLast = i === lastIndex;
        // The final "Shipped" step turns green once reached to signal completion;
        // every other reached step uses the brand brown.
        const filledClass =
          isLast && reached
            ? "bg-green-600 border-green-600 text-white"
            : reached
              ? "bg-brown border-brown text-cream"
              : "bg-gray-100 border-gray-300 text-gray-400";

        return (
          <li
            key={step.key}
            className="flex sm:flex-col sm:flex-1 items-center gap-3 sm:gap-0"
          >
            {/* Dot + connector halves. On sm+, the left/right half-lines of
                adjacent steps join into one connector between dots; the first
                step's left half and the last step's right half stay invisible. */}
            <div className="flex items-center sm:w-full">
              <span
                aria-hidden
                className={`hidden sm:block flex-1 h-0.5 ${
                  i === 0 ? "invisible" : reached ? "bg-brown" : "bg-gray-200"
                }`}
              />
              <span
                className={`flex items-center justify-center w-7 h-7 rounded-full border-2 text-xs font-semibold shrink-0 ${filledClass}`}
              >
                {reached ? "✓" : i + 1}
              </span>
              <span
                aria-hidden
                className={`hidden sm:block flex-1 h-0.5 ${
                  isLast
                    ? "invisible"
                    : i < current
                      ? "bg-brown"
                      : "bg-gray-200"
                }`}
              />
            </div>
            <span
              className={`sm:mt-2 text-sm sm:text-center opensans-text ${
                reached ? "text-brown-dark font-semibold" : "text-gray-400"
              }`}
            >
              {step.label}
            </span>
          </li>
        );
      })}
    </ol>
  );
};

export default OrderTimeline;
